import { createFocusTrap } from "../../JavaScript/utils/focusTrap";

export function initHeader(root) {
    const header = root?.closest?.(".header") ?? root;
    if (!(header instanceof HTMLElement)) return;

    const panel = header.querySelector(".header-main");
    const smartBar = header.querySelector(".header-smart");
    const toggler = header.querySelector(".menu-toggler");
    const desktopMq = window.matchMedia("(min-width: 64rem)");

    const isMobile = () => !desktopMq.matches;
    const isMenuOpen = () => header.classList.contains("is-menu-open");

    const getControlledMenu = (btn) => {
        if (!(btn instanceof HTMLElement)) return null;
        const id = btn.getAttribute("aria-controls");
        return id ? header.querySelector(`#${CSS.escape(id)}`) : null;
    };

    const getTriggerType = (btn) => {
        if (!(btn instanceof Element)) return null;

        if (
            btn.matches(
                '.header-navigation .header-context-menu-trigger[data-has-submenu="true"][aria-controls]'
            )
        ) {
            return "nav";
        }

        if (
            btn.matches(
                '.header-profile .header-context-menu-trigger[aria-controls], .header-loan-switcher .header-context-menu-trigger[aria-controls]'
            )
        ) {
            return "context";
        }

        return null;
    };

    const getDropdownRoot = (btn) => {
        if (!(btn instanceof HTMLElement)) return null;
        return btn.closest(".has-submenu, .header-profile, .header-loan-switcher");
    };

    const getManagedTriggers = () =>
        Array.from(header.querySelectorAll(".header-context-menu-trigger[aria-controls][aria-expanded]"))
            .filter((btn) => btn instanceof HTMLElement && getTriggerType(btn));

    const setMenuToggleState = (open) => {
        if (!(toggler instanceof HTMLElement)) return;
        toggler.setAttribute("aria-expanded", String(open));
        toggler.setAttribute("aria-label", open ? "Menu sluiten" : "Menu openen");
    };

    const setPanelInteractiveState = (open) => {
        if (!(panel instanceof HTMLElement)) return;

        if (isMobile()) {
            panel.toggleAttribute("inert", !open);

            if (open) {
                panel.setAttribute("role", "dialog");
                panel.setAttribute("aria-modal", "true");
            } else {
                panel.removeAttribute("role");
                panel.removeAttribute("aria-modal");
            }

            return;
        }

        panel.removeAttribute("inert");
        panel.removeAttribute("role");
        panel.removeAttribute("aria-modal");
    };

    const setInertOutsidePanel = (enabled) => {
        Array.from(header.children).forEach((el) => {
            if (!(el instanceof HTMLElement)) return;
            if (el === panel || el === smartBar) return;

            if (enabled) {
                el.setAttribute("inert", "");
            } else {
                el.removeAttribute("inert");
            }
        });
    };

    const closeDropdown = (btn, { restoreFocus = false } = {}) => {
        if (!(btn instanceof HTMLElement)) return;

        const root = getDropdownRoot(btn);
        const menu = getControlledMenu(btn);
        const activeInside = menu instanceof HTMLElement && menu.contains(document.activeElement);

        btn.setAttribute("aria-expanded", "false");
        root?.classList.remove("is-open");

        if (restoreFocus && activeInside) {
            btn.focus({ preventScroll: true });
        }
    };

    const openDropdown = (btn) => {
        if (!(btn instanceof HTMLElement)) return;

        const root = getDropdownRoot(btn);
        if (!root) return;

        btn.setAttribute("aria-expanded", "true");
        root.classList.add("is-open");
    };

    const closeAllDropdowns = ({ except = null, restoreFocus = false } = {}) => {
        getManagedTriggers().forEach((btn) => {
            if (btn === except) return;
            closeDropdown(btn, { restoreFocus });
        });
    };

    const focusFirstItemInMenu = (btn) => {
        const menu = getControlledMenu(btn);
        const firstItem = menu?.querySelector("a, button");
        if (firstItem instanceof HTMLElement) {
            firstItem.focus({ preventScroll: true });
        }
    };

    const trap = createFocusTrap({
        containers: [smartBar, panel],
        initialFocus: toggler,
        onEscape: () => closeMenu()
    });

    const openMenu = () => {
        if (!(panel instanceof HTMLElement) || !isMobile() || isMenuOpen()) return;

        header.classList.add("is-menu-open", "open");

        if (!panel.id) panel.id = "header-main";
        if (toggler instanceof HTMLElement) {
            toggler.setAttribute("aria-controls", panel.id);
        }

        setPanelInteractiveState(true);
        setMenuToggleState(true);
        setInertOutsidePanel(true);
        trap.activate();
    };

    const closeMenu = () => {
        if (!(panel instanceof HTMLElement) || !isMenuOpen()) return;

        header.classList.remove("is-menu-open", "open");

        trap.deactivate();
        setPanelInteractiveState(false);
        setMenuToggleState(false);
        setInertOutsidePanel(false);
        closeAllDropdowns();
    };

    const toggleMenu = () => {
        if (isMenuOpen()) {
            closeMenu();
        } else {
            openMenu();
        }
    };

    const toggleDropdown = (btn, { focusMenu = false } = {}) => {
        const isOpen = btn.getAttribute("aria-expanded") === "true";

        closeAllDropdowns({ except: btn });

        if (isOpen) {
            closeDropdown(btn);
            return;
        }

        openDropdown(btn);

        if (focusMenu) {
            focusFirstItemInMenu(btn);
        }
    };

    const onHeaderClick = (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;

        const btn = target.closest(".header-context-menu-trigger[aria-controls][aria-expanded]");
        if (!(btn instanceof HTMLElement) || !header.contains(btn)) return;
        if (!getTriggerType(btn)) return;

        e.preventDefault();
        e.stopPropagation();

        // Remove no-hover class to restore hover behavior
        const menu = getControlledMenu(btn);
        if (menu instanceof HTMLElement) {
            menu.classList.remove("no-hover");
        }

        toggleDropdown(btn, { focusMenu: !isMobile() });
    };

    const onHeaderKeydown = (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;

        const btn = target.closest(".header-context-menu-trigger[aria-controls][aria-expanded]");
        if (!(btn instanceof HTMLElement) || !header.contains(btn)) return;
        if (!getTriggerType(btn)) return;

        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleDropdown(btn, { focusMenu: !isMobile() });
            return;
        }

        if (e.key === "ArrowDown") {
            e.preventDefault();
            closeAllDropdowns({ except: btn });
            openDropdown(btn);
            focusFirstItemInMenu(btn);
            return;
        }

        if (e.key === "Escape") {
            e.preventDefault();
            closeDropdown(btn, { restoreFocus: true });
        }
    };

    const onDocumentClick = (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;

        if (!header.contains(target)) {
            closeAllDropdowns();

            if (isMenuOpen()) {
                closeMenu();
            }
        }
    };

    const onDocumentKeydown = (e) => {
        if (isMenuOpen()) return;

        if (e.key === "Escape") {
            const expanded = header.querySelector(
                '.header-context-menu-trigger[aria-expanded="true"]'
            );

            if (expanded instanceof HTMLElement && getTriggerType(expanded)) {
                e.preventDefault();
                closeAllDropdowns({ restoreFocus: true });
            }

            // On desktop, also close any hovered dropdowns by removing hover state
            if (desktopMq.matches) {
                getManagedTriggers().forEach((btn) => {
                    const root = getDropdownRoot(btn);
                    const menu = getControlledMenu(btn);
                    if (root instanceof HTMLElement && menu instanceof HTMLElement) {
                        root.classList.remove("is-open");
                        // Apply no-hover class to prevent hover from showing the menu
                        menu.classList.add("no-hover");
                    }
                });
            }
        }
    };

    const onBreakpointChange = () => {
        if (desktopMq.matches && isMenuOpen()) {
            closeMenu();
        }

        setPanelInteractiveState(isMenuOpen());
        setMenuToggleState(isMenuOpen());
        closeAllDropdowns();

        // Sync focus trap activation with visible header
        if (desktopMq.matches) {
            trap.deactivate();
        } else if (isMenuOpen()) {
            trap.activate();
        }
    };

    // Add ResizeObserver as fallback to detect zoom changes
    let resizeObserverTimeout;
    const resizeObserver = new ResizeObserver(() => {
        clearTimeout(resizeObserverTimeout);
        resizeObserverTimeout = setTimeout(() => {
            onBreakpointChange();
        }, 100);
    });

    if (header instanceof HTMLElement) {
        resizeObserver.observe(header);
    }

    if (toggler instanceof HTMLElement) {
        toggler.setAttribute("type", "button");
        toggler.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleMenu();
        });
    }

    if (panel instanceof HTMLElement) {
        panel.removeAttribute("aria-hidden");
        setPanelInteractiveState(false);
    }

    header.querySelectorAll(".header-context-menu").forEach((menu) => {
        if (menu instanceof HTMLElement) {
            menu.removeAttribute("aria-hidden");
        }
    });

    // Add mouseenter handler to restore hover behavior when user hovers again after escape
    header.querySelectorAll(".header-context-menu-trigger[aria-expanded]").forEach((btn) => {
        if (btn instanceof HTMLElement) {
            btn.addEventListener("mouseenter", () => {
                if (desktopMq.matches) {
                    const menu = getControlledMenu(btn);
                    if (menu instanceof HTMLElement) {
                        menu.classList.remove("no-hover");
                    }
                }
            });
        }
    });

    Array.from(header.children).forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (el === panel || el === smartBar) return;
        el.removeAttribute("aria-hidden");
    });

    header.addEventListener("click", onHeaderClick);
    header.addEventListener("keydown", onHeaderKeydown);
    document.addEventListener("click", onDocumentClick);
    document.addEventListener("keydown", onDocumentKeydown, true);
    desktopMq.addEventListener("change", onBreakpointChange);

    setMenuToggleState(false);
    onBreakpointChange();
}